import { create } from "zustand";

// Backend API types (matching LibraryColorPanel)
interface BackendColorToken {
  id: string;
  name: string;
  value: string;
  color: string;
}

interface BackendColorGroup {
  id: string;
  name: string;
  isExpanded: boolean;
  colors: BackendColorToken[];
}

// Frontend types (matching LibraryColorPanel)
interface TailwindColor {
  name: string;
  path: string; // e.g., 'primary.DEFAULT', 'secondary.foreground'
  rawValue: string; // e.g., 'hsl(var(--destructive))', '#ff0000'
  resolvedValue?: string; // e.g., 'hsl(0 84.2% 60.2%)', resolved from CSS variables
  hexValue?: string; // e.g., '#ef4444', converted from HSL
  group: string;
  displayValue?: string; // Added for backend display
}

interface ColorGroup {
  name: string;
  colors: TailwindColor[];
}

interface ColorStore {
  // Cache colors by canvasId
  colorCache: Record<string, ColorGroup[]>;

  // Single loading state
  loading: boolean;

  // Actions
  getColors: (canvasId: string) => ColorGroup[] | null;
  setColors: (canvasId: string, colorGroups: ColorGroup[]) => void;
  isLoading: () => boolean;
  setLoading: (loading: boolean) => void;
}

export const useColorStore = create<ColorStore>((set, get) => ({
  colorCache: {},
  loading: false,

  getColors: (canvasId: string) => {
    const { colorCache } = get();
    return colorCache[canvasId] || null;
  },

  setColors: (canvasId: string, colorGroups: ColorGroup[]) => {
    set((state) => ({
      colorCache: {
        ...state.colorCache,
        [canvasId]: colorGroups,
      },
    }));
  },

  isLoading: () => {
    const { loading } = get();
    return loading;
  },

  setLoading: (loading: boolean) => {
    set({ loading });
  },

  clearCache: (canvasId?: string) => {
    if (canvasId) {
      set((state) => {
        const newCache = { ...state.colorCache };
        delete newCache[canvasId];
        return { colorCache: newCache };
      });
    } else {
      set({ colorCache: {} });
    }
  },
}));

export type { ColorGroup, TailwindColor, BackendColorGroup, BackendColorToken };
